import React from 'react';
import {
    Search,
    Bookmark,
    Cpu,
    X,
    FlaskConical,
    Sparkles
} from 'lucide-react';
import { ThemeToggle } from '../ThemeToggle';

import type { Topic } from '../types';

interface SidebarProps {
    topics: Topic[];
    activeTopic: string;
    setActiveTopic: (topic: string) => void;
    customTopic: string;
    setCustomTopic: (topic: string) => void;
    bookmarksCount: number;
    view: string;
    setView: (view: string) => void;
    mobileMenuOpen: boolean;
    setMobileMenuOpen: (open: boolean) => void;
    onAddTopic: (e: React.FormEvent) => void;
    onRemoveTopic: (topic: string) => void;
}

export const Sidebar = ({
    topics,
    activeTopic,
    setActiveTopic,
    customTopic,
    setCustomTopic,
    bookmarksCount,
    view,
    setView,
    mobileMenuOpen,
    setMobileMenuOpen,
    onAddTopic,
    onRemoveTopic
}: SidebarProps) => {
    return (
        <aside className={`
      fixed inset-0 z-10 bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 w-full sm:w-80 md:w-72 lg:w-80 md:static md:block transition-transform duration-300 ease-in-out
      ${mobileMenuOpen ? 'translate-x-0 pt-20' : '-translate-x-full md:translate-x-0'}
      flex flex-col h-full
    `}>
            <div className="p-6 hidden md:block">
                <div className="flex items-center gap-2 font-bold text-indigo-600 mb-8">
                    <FlaskConical size={28} />
                    <span className="text-2xl tracking-tight">Eolas Online</span>
                </div>
            </div>

            <div className="px-4 mb-6">
                <form onSubmit={onAddTopic} className="relative group">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500" size={16} />
                    <input
                        type="text"
                        placeholder="Add topic (e.g. CRISPR)..."
                        className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                        value={customTopic}
                        onChange={(e) => setCustomTopic(e.target.value)}
                    />
                </form>
            </div>

            <div className="flex-grow overflow-y-auto px-4 pb-4 space-y-1">
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 px-2">Library</div>
                <button
                    onClick={() => { setView('bookmarks'); setMobileMenuOpen(false); }}
                    className={`w-full flex items-center gap-3 px-3 py-3 sm:py-2.5 rounded-lg text-sm font-medium transition-all touch-manipulation
            ${view === 'bookmarks' ? 'bg-amber-50 text-amber-700' : 'text-slate-600 hover:bg-slate-50 active:bg-slate-100'}`}
                >
                    <Bookmark size={18} className={view === 'bookmarks' ? 'fill-amber-700' : ''} />
                    Saved Papers
                    {bookmarksCount > 0 && (
                        <span className="ml-auto bg-amber-100 text-amber-800 text-[10px] px-1.5 py-0.5 rounded-full font-bold">
                            {bookmarksCount}
                        </span>
                    )}
                </button>

                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mt-6 mb-2 px-2">Feeds</div>
                {topics.map(topic => (
                    <button
                        key={topic.name}
                        onClick={() => { setActiveTopic(topic.name); setView('feed'); setMobileMenuOpen(false); }}
                        className={`w-full flex items-center justify-between group px-3 py-3 sm:py-2.5 rounded-lg text-sm font-medium transition-all touch-manipulation
              ${activeTopic === topic.name && view === 'feed' ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-50'}`}
                    >
                        <div className="flex items-center gap-3 truncate">
                            <Cpu size={18} className={activeTopic === topic.name && view === 'feed' ? 'text-indigo-600' : 'text-slate-400'} />
                            <span className="truncate">{topic.name}</span>
                        </div>
                        {topics.length > 1 && (
                            <div
                                onClick={(e) => { e.stopPropagation(); onRemoveTopic(topic.name); }}
                                className="opacity-0 group-hover:opacity-100 p-1 hover:bg-rose-100 hover:text-rose-600 rounded transition-all"
                            >
                                <X size={12} />
                            </div>
                        )}
                    </button>
                ))}
            </div>

            <div className="p-4 border-t border-slate-100 dark:border-slate-700">
                <ThemeToggle />
                <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-4 text-white">
                    <div className="flex items-center gap-2 mb-2">
                        <Sparkles size={16} className="text-yellow-300" />
                        <span className="font-bold text-sm">Pro Tip</span>
                    </div>
                    <p className="text-xs text-indigo-100 leading-relaxed opacity-90">
                        Use the top filter bar to search by institution, author, or keyword.
                    </p>
                </div>
            </div>
        </aside>
    );
};
